import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  InputGroup,
  InputRightAddon,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateAgreementCompanyMutation } from '@partiu/api/dashboard/hooks';
import { useCategories } from '@partiu/api/categories/hooks.ts';
import { useGetCEPMutation, useGetCNPJMutation } from '@partiu/api/thirdParty/hooks.ts';
import { IStore } from '@partiu/api/types';
import { cleanCEP, cleanCPFCNPJ } from '@partiu/api/utils.ts';
import { ImageInput, Input, PostalCodeInput } from '@partiu/ui/components/atoms/Input.tsx';
import { PropsWithChildren, ReactNode, useState } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { FaCheck, FaPercent } from 'react-icons/fa';
import { z } from 'zod';
import { AddModalProps } from '../../../types';

const RULES = [
  'Não cumulativa com outras promoções.',
  'Todos os dias e horários.',
  'Exceto Delivery.',
  'Promoção válida somente para essa unidade.',
  'Sujeito a disponibilidade do produto e lotação do estabelecimento.',
];

function formatDocument(value: string) {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11)
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

const isLocal = (d: { mode: string }) => d.mode === 'local';
const ADDRESS_FIELDS = ['postal_code', 'street', 'neighborhood', 'city', 'state'] as const;

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;

const schema = z
  .object({
    cnpj: z.string().refine((v) => [11, 14].includes(cleanCPFCNPJ(v).length), 'Informe um CPF ou CNPJ completo'),
    corporate_name: z.string().trim().min(2, 'Informe a razão social').max(255),
    name: z.string().trim().min(2, 'Informe o nome fantasia').max(255),
    category: z.string().min(1, 'Selecione uma categoria'),
    description: z.string().max(500, 'Máximo de 500 caracteres').optional(),
    site: z.string().url('Use uma URL completa, ex: https://loja.com.br').optional().or(z.literal('')),
    exclusive: z.boolean(),
    mode: z.enum(['local', 'online']),
    postal_code: z.string().optional(),
    street: z.string().trim().optional(),
    number: z.string().regex(/^\d*$/, 'Apenas números').optional(),
    neighborhood: z.string().trim().optional(),
    city: z.string().trim().optional(),
    state: z.string().trim().optional(),
    link: z.string().url('Use uma URL completa, ex: https://loja.com.br/promo').optional().or(z.literal('')),
    coupon: z.string().trim().max(30).optional(),
    owner: z.string().trim().min(2, 'Informe o nome do responsável').max(255),
    email: z.string().email('Email inválido'),
    telephone: z.string().refine((v) => v.replace(/\D/g, '').length >= 10, 'Telefone com DDD'),
    type: z.enum(['discount', 'buy_and_gain', 'treat'], 'Escolha o tipo'),
    discount: z.string().optional(),
    discount_name: z.string().trim().min(3, 'Informe o título do benefício').max(60, 'Máximo de 60 caracteres'),
    rules: z.array(z.string()),
    other_rules: z.string().optional(),
  })
  .superRefine((d, ctx) => {
    const issue = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });

    if (d.type === 'discount' && !(Number(d.discount) >= 1 && Number(d.discount) <= 100))
      issue('discount', 'Informe uma porcentagem entre 1 e 100');

    if (isLocal(d)) {
      if (cleanCEP(d.postal_code ?? '').length !== 8) issue('postal_code', 'CEP incompleto');
      ADDRESS_FIELDS.slice(1).forEach((f) => (d[f] ?? '').length < 2 && issue(f, 'Campo obrigatório'));
    } else {
      if (!d.link) issue('link', 'Informe o link da promoção');
      if (!d.coupon) issue('coupon', 'Informe o cupom');
    }
  });

type FormData = z.infer<typeof schema>;
type ImageKey = 'logo' | 'hero' | 'product';

const IMAGES: [ImageKey, string][] = [
  ['logo', 'Logo'],
  ['hero', 'Capa (mín. 1400x300)'],
  ['product', 'Produto ou serviço'],
];

function Field({
  label,
  error,
  isRequired,
  children,
}: PropsWithChildren<{ label: string; error?: FieldError; isRequired?: boolean }>) {
  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      <FormLabel fontSize={14} marginBottom={1}>
        {label}
      </FormLabel>
      {children}
      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  );
}

function Section({ title, subtitle, children }: PropsWithChildren<{ title: string; subtitle?: ReactNode }>) {
  return (
    <VStack align="stretch" spacing={3} width="100%" paddingY={4} borderTop="1px solid" borderColor="gray.100">
      <VStack align="flex-start" spacing={0}>
        <Heading fontSize={16}>{title}</Heading>
        {subtitle && (
          <Text fontSize={13} color="gray.500">
            {subtitle}
          </Text>
        )}
      </VStack>
      {children}
    </VStack>
  );
}

function backendErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === 'object') {
    const [field, messages] = Object.entries(data)[0] ?? [];
    const message = Array.isArray(messages) ? messages[0] : messages;
    if (field && message) return `${field}: ${message}`;
  }
  return 'Verifique os dados e tente novamente ou fale com suporte@partiu.com.br.';
}

export function AgreementCompanyForm({ toast, onClose, onModelAdded }: AddModalProps<IStore>) {
  const { data: categories = [] } = useCategories();
  const [images, setImages] = useState<Partial<Record<ImageKey, File>>>({});

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { type: 'discount', mode: 'local', exclusive: true, rules: [] },
  });

  const fill = (values: Partial<Record<keyof FormData, string | undefined>>) =>
    Object.entries(values).forEach(([key, value]) => {
      if (value) setValue(key as keyof FormData, value, { shouldValidate: true });
    });

  const cepMutation = useGetCEPMutation({
    onSuccess(data) {
      if (!data) return;
      fill({ street: data.logradouro, neighborhood: data.bairro, city: data.localidade, state: data.uf });
    },
  });

  const cnpjMutation = useGetCNPJMutation({
    onSuccess(data) {
      if (!data) return;
      fill({
        corporate_name: data.nome,
        name: data.fantasia || data.nome,
        email: data.email,
        telephone: data.telefone?.split('/')[0]?.trim(),
        owner: data.qsa?.[0]?.nome,
        postal_code: data.cep,
        street: data.logradouro,
        number: data.numero?.replace(/\D/g, ''),
        neighborhood: data.bairro,
        city: data.municipio,
        state: data.uf,
      });
    },
  });

  const createMutation = useCreateAgreementCompanyMutation({
    onSuccess() {
      toast({
        title: 'Loja cadastrada',
        description: 'O lojista receberá um email com os próximos passos. A Partiu fará a aprovação.',
        status: 'success',
      });
      onModelAdded?.();
      onClose?.();
    },
    onError(error) {
      toast({ title: 'Não foi possível cadastrar a loja', description: backendErrorMessage(error), status: 'error' });
    },
  });

  function onImageChange(key: ImageKey, file?: File) {
    if (file && (file.size > MAX_IMAGE_SIZE || !['image/png', 'image/jpeg'].includes(file.type))) {
      toast({ title: 'Imagem inválida', description: 'Use PNG ou JPG de até 2MB.', status: 'error' });
      return;
    }
    setImages((prev) => ({ ...prev, [key]: file }));
  }

  function onSubmit({ rules, other_rules, discount, number, link, coupon, ...data }: FormData) {
    const local = isLocal(data);
    createMutation.mutate({
      ...(local
        ? data
        : Object.fromEntries(Object.entries(data).filter(([k]) => !ADDRESS_FIELDS.includes(k as never)))),
      ...images,
      rules: [...rules, other_rules?.trim()].filter(Boolean).join('\n'),
      discount: data.type === 'discount' ? discount : undefined,
      number: (local && number) || undefined,
      postal_code: local ? cleanCEP(data.postal_code ?? '') : undefined,
      link: local ? undefined : link,
      coupon: local ? undefined : coupon,
    });
  }

  const [type, mode] = watch(['type', 'mode']);

  return (
    <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Text fontSize={14} color="gray.600" marginTop="-10px" marginBottom={2}>
        Com CNPJ, preenchemos o resto automaticamente. Após o cadastro, o lojista recebe um email para acessar o sistema
        e a Partiu faz a aprovação.
      </Text>

      <Section title="Empresa">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Field label="CPF ou CNPJ" error={errors.cnpj} isRequired>
            <InputGroup>
              <Input
                inputMode="numeric"
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                {...register('cnpj', {
                  setValueAs: formatDocument,
                  onChange: (e) => {
                    e.target.value = formatDocument(e.target.value);
                    const document = cleanCPFCNPJ(e.target.value);
                    if (document.length === 14) cnpjMutation.mutate(document);
                  },
                })}
              />
              {cnpjMutation.isLoading && <InputRightAddon children={<Spinner size="sm" />} />}
            </InputGroup>
          </Field>
          <Field label="Categoria" error={errors.category} isRequired>
            <Select placeholder="Selecione" {...register('category')}>
              {categories.map((c: { id: number; name: string }) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Razão social ou nome completo" error={errors.corporate_name} isRequired>
            <Input {...register('corporate_name')} />
          </Field>
          <Field label="Nome fantasia" error={errors.name} isRequired>
            <Input {...register('name')} />
          </Field>
          <Field label="Site" error={errors.site}>
            <Input placeholder="https://" {...register('site')} />
          </Field>
          <Field label="Onde o associado usa o benefício?" isRequired>
            <RadioGroup value={mode} onChange={(v) => setValue('mode', v as FormData['mode'])}>
              <HStack spacing={6} height="44px">
                <Radio value="local">Loja física</Radio>
                <Radio value="online">Online</Radio>
              </HStack>
            </RadioGroup>
          </Field>
        </SimpleGrid>
        <Checkbox {...register('exclusive')}>
          <Text fontSize={14}>Exclusiva do meu convênio (não aparece para outros clubes Partiu)</Text>
        </Checkbox>
        <Field label="Descrição" error={errors.description}>
          <Textarea rows={3} placeholder="Conte um pouco sobre a loja" {...register('description')} />
        </Field>
      </Section>

      <Section title="Responsável" subtitle="Quem receberá o acesso ao sistema do lojista">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Nome completo" error={errors.owner} isRequired>
            <Input {...register('owner')} />
          </Field>
          <Field label="Email" error={errors.email} isRequired>
            <Input type="email" {...register('email')} />
          </Field>
          <Field label="Telefone" error={errors.telephone} isRequired>
            <Input type="tel" placeholder="(11) 99999-9999" {...register('telephone')} />
          </Field>
        </SimpleGrid>
      </Section>

      {mode === 'local' && (
        <Section title="Endereço da loja">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Field label="CEP" error={errors.postal_code} isRequired>
              <PostalCodeInput
                {...register('postal_code', {
                  onChange: (e) => {
                    const cep = cleanCEP(e.target.value);
                    if (cep.length === 8) cepMutation.mutate(cep);
                  },
                })}
              />
            </Field>
            <Field label="Número" error={errors.number}>
              <Input inputMode="numeric" placeholder="S/N" {...register('number')} />
            </Field>
            <Field label="Cidade" error={errors.city} isRequired>
              <Input {...register('city')} />
            </Field>
            <Field label="UF" error={errors.state} isRequired>
              <Input maxLength={2} textTransform="uppercase" {...register('state')} />
            </Field>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Field label="Rua" error={errors.street} isRequired>
              <Input {...register('street')} />
            </Field>
            <Field label="Bairro" error={errors.neighborhood} isRequired>
              <Input {...register('neighborhood')} />
            </Field>
          </SimpleGrid>
        </Section>
      )}

      <Section title="Benefício" subtitle="O que o associado ganha ao usar o clube nessa loja">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Tipo" error={errors.type} isRequired>
            <Select {...register('type')}>
              <option value="discount">Porcentagem de desconto</option>
              <option value="buy_and_gain">Compre e ganhe</option>
              <option value="treat">Brinde</option>
            </Select>
          </Field>
          {type === 'discount' && (
            <Field label="Desconto" error={errors.discount} isRequired>
              <InputGroup>
                <Input type="number" min={1} max={100} {...register('discount')} />
                <InputRightAddon children={<FaPercent />} />
              </InputGroup>
            </Field>
          )}
          <Field label="Título no app" error={errors.discount_name} isRequired>
            <Input placeholder="Ex: 10% na conta" maxLength={60} {...register('discount_name')} />
          </Field>
        </SimpleGrid>
        {mode === 'online' && (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Field label="Link da promoção" error={errors.link} isRequired>
              <Input placeholder="https://" {...register('link')} />
            </Field>
            <Field label="Cupom" error={errors.coupon} isRequired>
              <Input placeholder="Ex: PARTIU10" maxLength={30} {...register('coupon')} />
            </Field>
          </SimpleGrid>
        )}
        <FormControl>
          <FormLabel fontSize={14}>Regras</FormLabel>
          <CheckboxGroup onChange={(values) => setValue('rules', values as string[])}>
            <Stack spacing={1}>
              {RULES.map((rule) => (
                <Checkbox key={rule} value={rule}>
                  <Text fontSize={14}>{rule}</Text>
                </Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
          <Textarea marginTop={2} rows={2} placeholder="Outras regras (opcional)" {...register('other_rules')} />
        </FormControl>
      </Section>

      <Section title="Imagens" subtitle="Opcional — PNG ou JPG até 2MB. O lojista também pode enviar depois.">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {IMAGES.map(([key, label]) => (
            <FormControl key={key}>
              <FormLabel fontSize={14}>{label}</FormLabel>
              <ImageInput
                value={images[key]}
                onImageChange={(file) => onImageChange(key, file)}
                imageProps={{ maxHeight: '150px', objectFit: 'cover' }}
              />
            </FormControl>
          ))}
        </SimpleGrid>
      </Section>

      {Object.keys(errors).length > 0 && (
        <Alert status="warning" borderRadius={8} marginBottom={3}>
          <AlertIcon />
          Revise os campos destacados.
        </Alert>
      )}

      <HStack justify="flex-end" paddingTop={2}>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" colorScheme="green" rightIcon={<FaCheck />} isLoading={createMutation.isLoading}>
          Cadastrar loja
        </Button>
      </HStack>
    </form>
  );
}
